export type GeolocationPlatform = "ios" | "android" | "desktop";

/** GeolocationPositionError.PERMISSION_DENIED */
export const GEOLOCATION_PERMISSION_DENIED_CODE = 1;

export function getGeolocationPlatform(): GeolocationPlatform {
  if (typeof navigator === "undefined") {
    return "desktop";
  }

  const ua = navigator.userAgent;
  const isIpadOs =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

  if (/iPad|iPhone|iPod/i.test(ua) || isIpadOs) {
    return "ios";
  }

  if (/Android/i.test(ua)) {
    return "android";
  }

  return "desktop";
}

export function isNativeMobileGeolocationPlatform(): boolean {
  const platform = getGeolocationPlatform();
  return platform === "ios" || platform === "android";
}

/**
 * iOS (and partially Android WebView) can report stale permission state.
 * A real position probe is the source of truth on these platforms.
 */
export function shouldProbeBeforeTrustingPermissionState(): boolean {
  return getGeolocationPlatform() !== "desktop";
}

/** Poll while permission blocks capture and the document is visible. */
export function getBlockedRecoveryIntervalMs(): number {
  switch (getGeolocationPlatform()) {
    case "ios":
      return 3_000;
    case "android":
      return 6_000;
    default:
      return 12_000;
  }
}

export function getRecoveryProbeTimeoutMs(): number {
  switch (getGeolocationPlatform()) {
    case "ios":
      return 8_000;
    case "android":
      return 6_000;
    default:
      return 5_000;
  }
}

/** Sheet open gate — one quick read; avoid stacking dual iOS strategies. */
export function getSheetOpenProbeTimeoutMs(): number {
  switch (getGeolocationPlatform()) {
    case "ios":
      return 4_000;
    case "android":
      return 3_500;
    default:
      return 2_500;
  }
}

/** User tapped "I've enabled location" — allow extra time on mobile. */
export function getRecheckProbeTimeoutMs(): number {
  switch (getGeolocationPlatform()) {
    case "ios":
      return 12_000;
    case "android":
      return 10_000;
    default:
      return 6_000;
  }
}

/** Minimum gap between non-urgent sync probes (battery + dedup). */
export function getSyncDebounceMs(): number {
  switch (getGeolocationPlatform()) {
    case "ios":
      return 2_500;
    case "android":
      return 2_000;
    default:
      return 1_500;
  }
}

export function shouldForceProbeOnVisibility(): boolean {
  const platform = getGeolocationPlatform();
  return platform === "ios" || platform === "android";
}

/** Installed/standalone PWA (home-screen app) vs browser tab. */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const iosStandalone =
    (navigator as {standalone?: boolean}).standalone === true;
  const displayModeStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  return iosStandalone || displayModeStandalone;
}

/**
 * iOS standalone PWAs do not re-evaluate a denied geolocation permission in the
 * live JS context after the user enables it in Settings — only a reload (fresh
 * web view) recovers. In that case, recheck should reload rather than re-probe.
 */
export function requiresReloadToRecoverLocation(): boolean {
  return getGeolocationPlatform() === "ios" && isStandalonePwa();
}
